import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { submitLogin ,submitLogout } from '@/helpers/api/user';
import type { Credentials } from '@/helpers/api/user';
import router, { isReturnableRoute } from '@/router';
import { useRoute, START_LOCATION } from 'vue-router';
import _pick from 'lodash/pick';
import UserFlagsMapping from '@/data/mappings/UserFlags.map';
import AccessLevelsMapping from '@/data/mappings/AccessLevels.map';
import { fetchAvailableIntegrationInstances } from '@/helpers/api/player-subscription/fetch';
import { bugsnagService } from '@/config/bugsnag';
import { hydrateFromServer, readBestKnownLocalTheme } from '@/components/ThemeProvider/theme';
import { normalizeUiTheme, type UiThemeId } from '@/components/ThemeProvider/themeConstants';
import { fetchUserById } from '@/helpers/api/users/users';

export const LAST_USER_ID_KEY = 'lastUserId';

export const useUserStore = defineStore('user', () => {
  const userId = ref();
  const username = ref();
  const firstName = ref();
  const lastName = ref();
  const isLoggedIn = ref(false);
  const isAdmin = ref();
  const fullName = computed(() => `${firstName.value} ${lastName.value}`)
  const isLoginCheckedLocaly = ref(false);
  const error = ref();
  const passwordResetErrorMessage = ref();
  const isLoading = ref(false);
  // const roles = ref();
  const flags = ref();
  const areaAccessLevels = ref();
  const assetTypeAccessLevels = ref();
  const accountId = ref();
  const featureFlags = ref<Record<string, boolean>>({});
  const jwtToken = ref<string | null>(null);
  const uiTheme = ref<UiThemeId | null>(null);
  let uiThemeFetchInFlight: Promise<void> | null = null;

  const route = useRoute();

  function persistUserToLocalStorage() {
    localStorage.setItem('user', JSON.stringify({
      username: username.value,
      userId: userId.value,
      firstName: firstName.value,
      lastName: lastName.value,
      isAdmin: isAdmin.value,
      flags: flags.value,
      areaAccessLevels: areaAccessLevels.value,
      assetTypeAccessLevels: assetTypeAccessLevels.value,
      accountId: accountId.value,
      featureFlags: featureFlags.value,
      jwtToken: jwtToken.value,
      uiTheme: uiTheme.value,
    }));
  }

  function setUiTheme(value: string) {
    uiTheme.value = normalizeUiTheme(value);
    persistUserToLocalStorage();
  }

  /** Update the cached display name (e.g. after the user edits their own profile). */
  function setName(first: string | null | undefined, last: string | null | undefined) {
    if (typeof first === 'string') firstName.value = first;
    if (typeof last === 'string') lastName.value = last;
    persistUserToLocalStorage();
  }

  function loginResponseHasUiTheme(user: { uiTheme?: string | null } | null | undefined) {
    return user != null && user.uiTheme != null && user.uiTheme !== '';
  }

  /** Reconcile theme with server; login bootstrap is optional hint only. */
  async function syncUiThemeFromServer(
    loginBootstrapUser?: { uiTheme?: string | null } | null,
  ) {
    if (!isLoggedIn.value || !userId.value) {
      return;
    }
    if (loginResponseHasUiTheme(loginBootstrapUser)) {
      const fromLogin = normalizeUiTheme(loginBootstrapUser!.uiTheme);
      uiTheme.value = fromLogin;
      hydrateFromServer(fromLogin);
    }
    if (uiThemeFetchInFlight) {
      return uiThemeFetchInFlight;
    }
    uiThemeFetchInFlight = (async () => {
      try {
        const user = await fetchUserById(userId.value);
        const serverTheme = normalizeUiTheme(user?.uiTheme);
        uiTheme.value = serverTheme;
        // The name is otherwise only refreshed at login; pick up edits made
        // elsewhere (another device, an admin) from this same fetch.
        if (typeof user?.firstName === 'string') firstName.value = user.firstName;
        if (typeof user?.lastName === 'string') lastName.value = user.lastName;
        persistUserToLocalStorage();
        hydrateFromServer(serverTheme);
      } catch {
        if (uiTheme.value) {
          hydrateFromServer(uiTheme.value);
        } else {
          hydrateFromServer(readBestKnownLocalTheme());
        }
      } finally {
        uiThemeFetchInFlight = null;
      }
    })();
    return uiThemeFetchInFlight;
  }

  function checkLogin() {
    if(isLoginCheckedLocaly.value === true) {
      return
    }
    
    const userFromLocalStorage_string = localStorage.getItem('user') || '';
    if(!userFromLocalStorage_string) {    // user wasn't logged in before
      isLoggedIn.value = false;
    } else {
      isLoggedIn.value = true;
      copyUserFromLocalToState();
    }
    isLoginCheckedLocaly.value = true;
    redirectIfRouteRequiresAuth();
    if (isLoggedIn.value) {
      void syncUiThemeFromServer();
    }
    return

    function copyUserFromLocalToState() {
      const userFromLocalStorage = JSON.parse(userFromLocalStorage_string);
      username.value = userFromLocalStorage.username;
      userId.value = userFromLocalStorage.userId;
      firstName.value = userFromLocalStorage.firstName;
      lastName.value = userFromLocalStorage.lastName;
      isAdmin.value = userFromLocalStorage.isAdmin;
      flags.value = userFromLocalStorage.flags;
      areaAccessLevels.value = userFromLocalStorage.areaAccessLevels;
      assetTypeAccessLevels.value = userFromLocalStorage.assetTypeAccessLevels;
      accountId.value = userFromLocalStorage.accountId;
      featureFlags.value = userFromLocalStorage.featureFlags ?? {};
      jwtToken.value = userFromLocalStorage.jwtToken ?? null;
      // roles.value = userFromLocalStorage.roles;
    }

    function redirectIfRouteRequiresAuth() {
      if (route.meta.requiresAuth && isLoggedIn.value === false) {
        router.push({ name: 'login' })
      }
    }
  }
  async function login({username: usernameInput, password}: Credentials) {
    try {
      isLoading.value = true;
      const responseData = await submitLogin({username: usernameInput, password})
      isLoading.value = false;

      bugsnagService.setUser(responseData.user.id, responseData.user.userName, responseData.user.firstName + ' ' + responseData.user.lastName)

      await checkAndUpdateFeatureFlags();

      username.value = responseData.user.userName;
      userId.value = responseData.user.id;
      firstName.value = responseData.user.firstName;
      lastName.value = responseData.user.lastName;
      isAdmin.value = responseData.user.isAdmin;
      flags.value = responseData.user.flags;
      areaAccessLevels.value = responseData.user.areaAccessLevels;
      assetTypeAccessLevels.value = responseData.user.assetTypeAccessLevels;
      accountId.value = responseData.user.roles?.[0]?.accountId;
      jwtToken.value = responseData.jwtToken ?? null;
      // roles.value = getRolesPrepared(responseData.user.roles);
      isLoggedIn.value = true;
      // Persist before anything awaits: LoginView navigates as soon as isLoggedIn
      // flips, and the router guard reads localStorage if checkLogin() re-runs.
      persistUserToLocalStorage();

      await syncUiThemeFromServer(responseData.user);
      persistUserToLocalStorage();

      error.value = null;
    } catch (err: any) {
      isLoading.value = false;
      error.value = err.message;
    }
  }

  /** Checks and updates feature flags based on the available integrations */
  async function checkAndUpdateFeatureFlags() {
    try {
      /** Integration Implementation Keys of the features to be flagged */
      const implementationKeys = ["liveStream"];
      const availableIntegrationsResponse = await fetchAvailableIntegrationInstances({
        filters: {
          implementationKeys,
        },
      });
      const availableIntegrations = availableIntegrationsResponse.results;
  
      const isLiveStreamIntegrationInstanceAvailable =
        availableIntegrations.some((integration: any) =>
          integration.integrationImplementation.implementationKey === "liveStream"
        );
      featureFlags.value["liveStream"] = isLiveStreamIntegrationInstanceAvailable;
    } catch (err: any) {
      if(err.message?.match(/\s403/)) {
        console.error(`Feature flags are not available for the user`);
        return;
      } else {
        console.error(`Error while checking and updating feature flags`, err);
        throw err;
      }
    }
  }

  // A session that expires on a page with several requests in flight fails all of
  // them at once. Share one logout between them: one LogOff call, one navigation.
  let logoutInFlight: Promise<void> | undefined;

  async function logout(options?: { keepRedirect?: boolean }) {
    if (logoutInFlight) return logoutInFlight;
    logoutInFlight = (async () => {
      try {
        await submitLogout()
      } catch(error) {
        console.error(`Error while submitting logout`, error)
      }
      // Lets LoginView drop a ?redirect= when a different user signs in next, including one
      // the router guard rebuilds after Back. Per tab, like the redirect itself.
      if (userId.value != null) {
        sessionStorage.setItem(LAST_USER_ID_KEY, String(userId.value));
      }
      clearSession();
      localStorage.removeItem('user');
      // Awaited: callers of logout() (an expired request, via api.ts) handle their
      // error only once the page has gone.
      await navigateToLogin(options);
    })();
    try {
      await logoutInFlight;
    } finally {
      logoutInFlight = undefined;
    }
  }

  // Reset explicitly rather than via this.$reset(): isLoggedOutHandler() calls
  // logout() without the store as `this`, and the reset plugin only covers the
  // returned state (featureFlags isn't returned, so it was never cleared).
  // isLoginCheckedLocaly stays true: localStorage is cleared alongside, so there is
  // nothing to re-read, and a re-run of checkLogin() from the router guard would
  // push a bare /login over the ?redirect= navigation below.
  function clearSession() {
    userId.value = undefined;
    username.value = undefined;
    firstName.value = undefined;
    lastName.value = undefined;
    isLoggedIn.value = false;
    isAdmin.value = undefined;
    error.value = undefined;
    passwordResetErrorMessage.value = undefined;
    isLoading.value = false;
    flags.value = undefined;
    areaAccessLevels.value = undefined;
    assetTypeAccessLevels.value = undefined;
    accountId.value = undefined;
    featureFlags.value = {};
    jwtToken.value = null;
    uiTheme.value = null;
  }

  function navigateToLogin(options?: { keepRedirect?: boolean }) {
    // Keep the redirect only when the session expired under the user mid-task
    // (same user, same account). A deliberate logout or data-source switch must
    // land on a clean /login, otherwise signing in as a different client sends
    // us to a player/campaign that account doesn't own.
    // Explicit === true so a stray argument from a click handler can never be
    // read as an opt-in.
    const { name, fullPath } = currentOrPendingRoute()
    // Already on /login (a late failure after an earlier logout got us here):
    // stay put, so its ?redirect= query survives.
    if (name === 'login') return Promise.resolve()
    // Only a page worth returning to becomes the redirect: '/login?redirect=/not-found'
    // is a dead end.
    const keepRedirect = options?.keepRedirect === true && isReturnableRoute(name)
    // replace, not push: leaves no authenticated entry behind for Back to hit,
    // which would re-trigger the router guard and rebuild the redirect query.
    return keepRedirect
      ? router.replace({ name: 'login', query: { redirect: fullPath } })
      : router.replace({ name: 'login' })
  }

  // A request can fail during the first navigation (e.g. the theme sync started by
  // checkLogin() in the router guard), while currentRoute is still the "/" start
  // location. The address bar already shows the page being loaded, so use that.
  function currentOrPendingRoute(): { name?: unknown, fullPath: string } {
    const current = router.currentRoute.value
    if (current !== START_LOCATION) return current
    const pending = router.resolve(router.options.history.location)
    return { name: pending.name, fullPath: pending.fullPath }
  }

  interface Role {
    roleAreaAccesses: RoleAreaAccess[];
    roleAssetTypeAccesses: RoleAssetTypeAccess[];
  }

  interface RoleAreaAccess {
    access: string | number;
    area: {
      name: string;
      areaType: string | number;
    }
  }

  interface RoleAssetTypeAccess {
    access: string | number;
    assetType: string | number;
  }

  function getRolesPrepared(rolesReceived: Role[]) {
    const rolesPrepared: Role[] = [];
    rolesReceived.forEach((roleReceived: Role) => {
      const roleAreaAccessesPrepared: RoleAreaAccess[] = [];
      roleReceived.roleAreaAccesses.forEach(roleAreaAccess => {
        const roleAreaAccessPrepared = _pick(roleAreaAccess, ['access', 'area.name', 'area.areaType']);
        roleAreaAccessesPrepared.push(roleAreaAccessPrepared);
      })
      const roleAssetTypeAccessesPrepared: RoleAssetTypeAccess[] = [];
      roleReceived.roleAssetTypeAccesses.forEach(roleAssetTypeAccess => {
        const roleAssetTypeAccessPrepared = _pick(roleAssetTypeAccess, ['access', 'assetType']);
        roleAssetTypeAccessesPrepared.push(roleAssetTypeAccessPrepared);
      });
      
      const rolePrepared: Role = {
        roleAreaAccesses: roleAreaAccessesPrepared,
        roleAssetTypeAccesses: roleAssetTypeAccessesPrepared,
      };
      rolesPrepared.push(rolePrepared);
    });
    return rolesPrepared;
  }
  
  function isFlagSet(value: number, flag: number) {
    return ((value & flag) == flag);
  }

  function hasFlag(flagName: keyof typeof UserFlagsMapping) {
    if(isLoggedOutHandler()) return false;
    const userFlags = flags.value;
    return isFlagSet(userFlags, UserFlagsMapping[flagName]);
  }

  function hasAccessToFeature(featureFlagName: string) {
    
    if(isLoggedOutHandler()) return false;
    const userEnabledFeatureFlags = Object.keys(featureFlags.value).filter(
      featureFlagNameI => featureFlags.value[featureFlagNameI]
    );
    return userEnabledFeatureFlags.some(featureFlagNameI => featureFlagNameI === featureFlagName);
  }

  function isSuperUser() {
    return hasFlag("SuperUser");
  }
  function isAccountAdmin() {
    return hasFlag("AccountAdmin");
  }
  function isAuditor() {
    return hasFlag("Auditor") || isSuperUser();
  }
  function isAnalytics() {
    return hasFlag("Analytics") || isSuperUser() || isAccountAdmin();
  }

  function hasReadAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Read"]);
  }

  function hasWriteAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Write"]);
  }

  function hasCreateAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Create"]);
  }

  function hasDeleteAccessToArea(areaName: string) {
    if(isLoggedOutHandler()) return false;
    const userAreaAccessLevelFlag = areaAccessLevels.value[areaName];
    return isFlagSet(userAreaAccessLevelFlag, AccessLevelsMapping["Delete"]);
  }

  type AssetType = "Image" | "Video" | "Slide";

  function hasReadAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Read"]);
  }

  function hasWriteAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Write"]);
  }

  function hasCreateAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Create"]);
  }

  function hasDeleteAccessToAssetType(assetType: AssetType) {
    if(isLoggedOutHandler()) return false;
    if (assetType === undefined) return true;
    const assetTypePrepared = assetType[0].toUpperCase() + assetType.substring(1);
    const userAssetTypeAccessLevel = assetTypeAccessLevels.value[assetTypePrepared];
    return isFlagSet(userAssetTypeAccessLevel, AccessLevelsMapping["Delete"]);
  }

  function hasReadAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Read"]);
  }

  function hasWriteAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Write"]);
  }

  function hasCreateAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Create"]);
  }

  function hasDeleteAccessToItem(item: any) {
    return isFlagSet(item.accessLevel, AccessLevelsMapping["Delete"]);
  }

  function isLoggedOutHandler() {
    if (!isLoggedIn.value) return true; // anonymous: no redirect
    
    if(areaAccessLevels.value === undefined || assetTypeAccessLevels.value === undefined) {
      // important for "Dashboard" page if old users that don't have assetTypeAccessLevels stored in localStorage
      // Involuntary, like an expired session, so the redirect is kept.
      logout({ keepRedirect: true });
      return true;
    }
  }

  return { 
    userId,
    username,
    firstName,
    lastName,
    isLoggedIn,
    isAdmin,
    // roles,
    areaAccessLevels,
    assetTypeAccessLevels,
    accountId,
    fullName,
    isLoginCheckedLocaly,
    jwtToken,
    uiTheme,

    login,
    logout,
    checkLogin,
    setUiTheme,
    setName,
    syncUiThemeFromServer,
    
    hasFlag,
    hasAccessToFeature,
    isSuperUser,
    isAccountAdmin,
    isAuditor,
    isAnalytics,

    hasReadAccessToArea,
    hasWriteAccessToArea,
    hasCreateAccessToArea,
    hasDeleteAccessToArea,

    hasReadAccessToAssetType,
    hasWriteAccessToAssetType,
    hasCreateAccessToAssetType,
    hasDeleteAccessToAssetType,

    hasReadAccessToItem,
    hasWriteAccessToItem,
    hasCreateAccessToItem,
    hasDeleteAccessToItem,

    error,
    isLoading,
    passwordResetErrorMessage
  }
});

export default useUserStore;